<template>
  <el-row class="d-flex">
    <h4 class="mt-auto">
      {{ engagementUsers.length }} &#183;
      {{ $t('users') }}
    </h4>
    <el-button
      v-if="userCanWriteEngagementAuthorization"
      :disabled="loading"
      class="ml-auto"
      type="primary"
      @click="showAddRoleAssignmentModal = true"
    >
      <h4>{{ $t('addUser') }}</h4>
    </el-button>
    <el-button
      v-if="userCanWriteEngagementRbac"
      :disabled="loading"
      type="primary"
      @click="applyUsersRbac"
    >
      <h4>
        {{ $t('syncRoles') }}
      </h4>
    </el-button>
  </el-row>
  <table-placeholder-loading v-if="loading" :columns="4" />
  <el-table v-else :data="engagementUsers">
    <el-table-column width="200">
      <template #header>
        {{ showUsersAsEmail ? $t('email') : $t('name') }}
      </template>
      <template #default="scope">
        {{ showUsersAsEmail ? scope.row.email : scope.row.name }}
      </template>
    </el-table-column>
    <el-table-column>
      <template #header>
        <el-row>
          <el-col :span="6">
            {{ $t('role') }}
          </el-col>
          <el-col :span="17">
            {{ $t('scope') }}
          </el-col>
        </el-row>
      </template>
      <template #default="scope">
        <el-row
          v-for="roleAssignment in scope.row.roleAssignments.sort(compareRoleAssignments) as Array<RoleAssignmentModel>"
          :key="roleAssignment.roleName"
        >
          <el-col :span="6">
            <span class="body-1">
              {{ roleAssignment.roleName }}
            </span>
          </el-col>
          <el-col :span="17">
            <span class="body-1">
              {{ roleAssignment.scope }}
            </span>
          </el-col>
          <el-col :span="1" class="d-flex align-items-center">
            <img
              v-if="userCanWriteEngagementAuthorization"
              :src="require(`@/assets/images/icons/new/delete.svg`)"
              height="12"
              width="12"
              class="ml-auto action-icon"
              @click="openRemoveRoleAssignmentModal(roleAssignment)"
            />
          </el-col>
        </el-row>
      </template>
    </el-table-column>
    <el-table-column v-if="userCanWriteEngagementAuthorization" width="55">
      <template #default="scope">
        <div class="d-flex align-items-center">
          <img
            :src="require(`@/assets/images/icons/bin.svg`)"
            class="icon-size-7 cursor-pointer"
            @click="removeUser(scope.row)"
          />
        </div>
      </template>
    </el-table-column>
  </el-table>
  <el-row class="d-flex">
    <base-toggle-button
      v-model="showUsersAsEmail"
      :active-text="$t('email')"
      :inactive-text="$t('name')"
      class="ml-auto"
    />
  </el-row>
  <add-user-role-assignment-modal
    :modal-visible="showAddRoleAssignmentModal"
    :users="allUsers"
    :roles="allRoles"
    :scope="GetScopeForEngagement(engagementId)"
    @add-roles="addRoleAssignments"
    @close="showAddRoleAssignmentModal = false"
    @create-user="createUser"
  />
  <base-confirmation-modal
    v-model="showRemoveRoleAssignmentModal"
    :content="$t('confirmRemoveUserFromEngagement')"
    :feedback-text="$t('engagementUserDeleted')"
    @confirm="removeRoleAssignment"
    @close="showRemoveRoleAssignmentModal = false"
  />
</template>
<script lang="ts">
import {
  ElRow,
  ElCol,
  ElTable,
  ElTableColumn,
  ElButton,
  ElMessage
} from 'element-plus';

import { computed, ComputedRef, onMounted, ref } from 'vue';
import AddUserRoleAssignmentModal from '@/components/modals/AddUserRoleAssignmentModal.vue';
import BaseToggleButton from '@/components/buttons/BaseToggleButton.vue';
import TablePlaceholderLoading from '@/components/tables/TablePlaceholderLoading.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import config from '@/config/config';
import {
  GetScopeForEngagement,
  GetScopeMaskForEngagement
} from '@/utils/scopeHelper';
import BaseConfirmationModal from '@/components/modals/BaseConfirmationModal.vue';
import {
  CreateOrUpdateUserModel,
  PrincipalType,
  RoleAssignmentAction,
  RoleAssignmentModel,
  RoleModel,
  UpdateRoleAssignmentModel,
  UserDetailsModel,
  UserModel
} from '@etp/etp-authorization-client/axios';
import i18n from '@/i18n';
import { compareRoleAssignments } from '@/utils/roleAssignmentFunctions';

export default {
  name: 'EngagementUsers',
  components: {
    BaseConfirmationModal,
    BaseToggleButton,
    AddUserRoleAssignmentModal,
    ElButton,
    ElRow,
    ElCol,
    ElTable,
    ElTableColumn,
    TablePlaceholderLoading
  },
  setup() {
    let engagementId = ref('');

    const router = useRouter();
    const store = useStore();

    const engagementUsers: ComputedRef<Array<UserDetailsModel>> = computed(
      () => store.getters['engagements/getEngagementUsers']
    );
    const allUsers: ComputedRef<Array<UserModel>> = computed(
      () => store.getters['users/getUsers']
    );
    const allRoles: ComputedRef<Array<RoleModel>> = computed(
      () => store.getters['roles/getRoles']
    );
    const showUsersAsEmail = ref(false);
    const loading = ref(false);

    const userCanWriteEngagementAuthorization: ComputedRef<boolean> = computed(
      () => store.getters['userPermissions/canWriteEngagementAuthorization']
    );
    const userCanWriteEngagementRbac: ComputedRef<boolean> = computed(
      () => store.getters['userPermissions/canWriteEngagementRbac']
    );

    const fetchEngagementUsers = async () =>
      await store.dispatch(
        'engagements/fetchEngagementUsers',
        engagementId.value
      );

    const roleAssignmentToRemove = ref({} as RoleAssignmentModel);
    const showRemoveRoleAssignmentModal = ref(false);
    const openRemoveRoleAssignmentModal = (
      roleAssignment: RoleAssignmentModel
    ) => {
      roleAssignmentToRemove.value = roleAssignment;
      showRemoveRoleAssignmentModal.value = true;
    };
    const removeRoleAssignment = async () => {
      loading.value = true;
      showRemoveRoleAssignmentModal.value = false;
      await store.dispatch('roles/updateRoleAssignments', [
        {
          principalId: roleAssignmentToRemove.value.principalId,
          principalType: PrincipalType.User,
          scope: roleAssignmentToRemove.value.scope,
          roleName: roleAssignmentToRemove.value.roleName,
          action: RoleAssignmentAction.Remove
        } as UpdateRoleAssignmentModel
      ]);
      if (
        config.cloudProvider.toLowerCase() !== 'aws' &&
        roleAssignmentToRemove.value.scope ==
          GetScopeForEngagement(engagementId.value)
      ) {
        const email = allUsers.value.find(
          u => u.aadObjectId === roleAssignmentToRemove.value.principalId
        )?.email;
        if (email) {
          await store.dispatch('engagements/applyUserRbac', {
            email: email
          });
        }
      }
      await fetchEngagementUsers();
      loading.value = false;
    };

    const createUser = async (user: UserModel) => {
      await store.dispatch('users/createOrUpdateUser', {
        email: user.email,
        authorizationModel: {
          name: user.name,
          disabled: user.disabled
        } as CreateOrUpdateUserModel
      });
    };

    const removeUser = async (user: UserDetailsModel) => {
      loading.value = true;
      await store.dispatch(
        'roles/updateRoleAssignments',
        user.roleAssignments.map((el: RoleAssignmentModel) => {
          return {
            principalId: el.principalId,
            principalType: PrincipalType.User,
            roleName: el.roleName,
            scope: el.scope,
            action: RoleAssignmentAction.Remove
          } as UpdateRoleAssignmentModel;
        })
      );
      if (config.cloudProvider.toLowerCase() !== 'aws') {
        await store.dispatch('engagements/applyUserRbac', {
          email: user.email
        });
      }
      await fetchEngagementUsers();
      loading.value = false;
    };

    const showAddRoleAssignmentModal = ref(false);
    const addRoleAssignments = async (
      userRoleAssignments: Array<UpdateRoleAssignmentModel>
    ) => {
      loading.value = true;
      showAddRoleAssignmentModal.value = false;
      await store.dispatch('roles/updateRoleAssignments', userRoleAssignments);
      if (config.cloudProvider.toLowerCase() !== 'aws') {
        await store.dispatch('engagements/applyUserRbac', {
          email: userRoleAssignments[0].principalId // This is email here
        });
      }
      ElMessage({
        showClose: true,
        message: i18n.global.t('rolesAddedSuccessfully')
      });
      await fetchEngagementUsers();
      loading.value = false;
    };

    const applyUsersRbac = async () => {
      loading.value = true;
      for (let user of engagementUsers.value) {
        await store.dispatch('engagements/applyUserRbac', {
          email: user.email
        });
      }
      loading.value = false;
      ElMessage({
        showClose: true,
        message: i18n.global.t('rolesSyncedSuccessfully')
      });
    };

    onMounted(async () => {
      loading.value = true;
      engagementId.value = router.currentRoute.value.params
        .engagementId as string;
      await fetchEngagementUsers();
      await store.dispatch('users/fetchAllUsers');
      await store.dispatch('roles/fetchAllRoles', GetScopeMaskForEngagement());
      await store.dispatch(
        'userPermissions/checkIfCanWriteEngagementAuthorization'
      );
      await store.dispatch('userPermissions/checkIfCanWriteEngagementRbac');
      loading.value = false;
    });

    return {
      addRoleAssignments,
      allUsers,
      allRoles,
      applyUsersRbac,
      compareRoleAssignments,
      createUser,
      GetScopeForEngagement,
      loading,
      openRemoveRoleAssignmentModal,
      removeRoleAssignment,
      engagementId,
      engagementUsers,
      removeUser,
      showAddRoleAssignmentModal,
      showRemoveRoleAssignmentModal,
      showUsersAsEmail,
      userCanWriteEngagementAuthorization,
      userCanWriteEngagementRbac
    };
  }
};
</script>
